import styles from './styles/SmartCitationsPanel.css'
import { Manager, Reference, Popper } from 'react-popper'
import Carousel from 'nuka-carousel'
import tooltipStyles from 'components/misc/styles/Tooltip.css'
import classNames from 'classnames'
import Authors from 'components/misc/Authors'
import PaperReference from 'components/report/Reference'
import { Link } from 'react-router-dom'
import { FadeVisual } from 'components/misc/Fade'
import Header from '../typography/Header'
import P from 'components/typography/Paragraph'
import Button from '../forms/Button'

const SmartCitationsPopper = ({
  number,
  placement,
  text,
  classes
}) => (
  <Manager>
    <Reference>
      {({ ref }) => (
        <span ref={ref} className={styles.smartCitationsTooltipIcon}>
          {number}
        </span>
      )}
    </Reference>
    <Popper
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4]
          }
        },
        {
          name: 'preventOverflow',
          options: {
            mainAxis: false
          }
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: []
          }
        }
      ]}
      placement={placement}
    >
      {/* The FadeVisual is so that it doesn't render the tooltip
          on the initial render because it will be in the wrong place.
          Not sure on a better solution for that, possibly something weird
          going on. */}
      {({ ref, style, placement }) => (
        <FadeVisual show>
          <div
            className={classNames(...classes)}
            ref={ref}
            style={style}
            data-placement={placement}
          >
            {text}
          </div>
        </FadeVisual>
      )}
    </Popper>
  </Manager>
)

const SmartCitationCarousel = () => (
  <div className={styles.carouselContainer}>
    <Carousel
      slidesToScroll={1}
      slidesToShow={1}
      dragging
      initialSlideHeight={10}
      heightMode='current'
      wrapAround
      framePadding='2rem'
      renderCenterLeftControls={({ previousSlide }) => (
        <div className={styles.carouselChevron} onClick={previousSlide}><i className='fa fa-arrow-left' /></div>
      )}
      renderCenterRightControls={({ nextSlide }) => (
        <div className={styles.carouselChevron} onClick={nextSlide}><i className='fa fa-arrow-right' /></div>
      )}
      renderBottomCenterControls={() => (<div />)}
    >
      <div key={0} className={styles.carouselItem}>
        <div className={styles.smartCitationsCarouselIcon}>
          1
        </div>
        <span>Our deep learning model classifies each citation context. The numbers show how sure it is of a classification.</span>
      </div>
      <div key={1} className={styles.carouselItem}>
        <div className={styles.smartCitationsCarouselIcon}>
          2
        </div>
        <span>You can find the exact citation context here. Clicking on the title of the citing paper takes you directly to the publication.</span>
      </div>
      <div key={3} className={styles.carouselItem}>
        <div className={styles.smartCitationsCarouselIcon}>
          3
        </div>
        <span>You can click on any citation in the excerpt to explore related articles.</span>
      </div>
      <div key={2} className={styles.carouselItem}>
        <div className={styles.smartCitationsCarouselIcon}>
          4
        </div>
        <span>You can see in which section of the citing paper the citation appeared.</span>
      </div>
    </Carousel>
  </div>
)

const SciteClassificationBlock = () => (
  <div className={styles.leftCol}>
    <div className={styles.citationDetails}>
      <div className={styles.typeInfo}>
        <i className={styles.icon + ' ' + styles.typeIcon + ' ' + styles['typeIcon--supporting']} />
        <span>supporting</span>
      </div>
      <div className={styles.classificationDetailsLayout}>
        <div className={styles.classificationDetails}>
          <span className={styles.classificationDetail}>Confidence {93}%</span>
          <SmartCitationsPopper
            number={1}
            placement='bottom-end'
            classes={[tooltipStyles.popperBase, styles.smartCitationsToolTip, styles.large, styles.tooltipBottomLeft]}
            text='Our deep learning model classifies each citation context automatically. The numbers show how sure it is of a classification.'
          />
        </div>
      </div>
    </div>
  </div>
)

const CitingPaperBlock = () => {
  const year = '2018'
  const authors = [{ family: 'Viganó', given: 'Christina' }, { family: 'von Schubert', given: 'Conrad' }]
  const shortJournal = 'Molecular Biology of the Cell'
  const issue = '9'
  const page = '1031-1047'
  const section = 'Results'
  const pmPublicationType = ''
  const title = 'Quantitative proteomic and phosphoproteomic comparison of human colon cancer DLD-1 cells differing in ploidy and chromosome stability'
  return (
    <div className={styles.citingPaperBlock}>
      <div className={styles.paperContainer}>
        <div className={styles.abstract}>
          “…Compared to the diploid parental line, the frequencies of chromosome
          <SmartCitationsPopper
            number={2}
            placement='right-end'
            classes={[tooltipStyles.popperBase, styles.smartCitationsToolTip, styles.large, styles.tooltipTopRight]}
            text='You can find the exact citation context here. Clicking on the title of the citing paper takes you directly to the publication.'
          /> missegregation and micronuclei formation were significantly elevated in most PTA clones (Figure 2A) but not in the tetraploid line (Figure 2A). <mark className={styles.markHighlight}>In agreement with previous work (<span className={styles.targetCite}>Nicholson et al , 2015</span>), the trisomic clones showed similar aberrations, albeit to a lesser extent (Supplemental Figure S2B).</mark> Furthermore, we observed an increase of structural aberrations in PTA lines and, consistent with earlier work (<i>Kuznetsova et al </i>
          <SmartCitationsPopper
            number={3}
            placement='right-start'
            classes={[tooltipStyles.popperBase, styles.smartCitationsToolTip, styles.large, styles.tooltipBottomRight]}
            text='You can click on any citation in the excerpt to explore related articles.'
          />
          <i> 2015</i>; <i>Passerini et al, 2016</i>
          )
          also in trisomic clones (Figure 2B).…”
        </div>

      </div>
      <div className={styles.paperContainer}>
        <div className={styles.bottomLine}>
          <a className={styles.headerLink} target='_blank' rel='noopener noreferrer' href='/reports/quantitative-proteomic-and-phosphoproteomic-comparison-RVae00p'>{title}</a>
          <div className={styles.authors}>
            <Authors authors={authors} year={year} truncated firstNames={false} />
          </div>
          <div className={styles.reference}>
            <PaperReference
              shortJournal={shortJournal}
              issue={issue}
              page={page}
              section={section}
              pmPublicationType={pmPublicationType}
            />
          </div>
          <SmartCitationsPopper
            number={4}
            placement='bottom-start'
            classes={[tooltipStyles.popperBase, styles.smartCitationsToolTip, styles.large, styles.tooltipGeneric]}
            text='You can see where the citation appeared.'
          />
          <div className={styles.subLinkLayout}>
            <Link className={styles.subLink} target='_blank' rel='noopener noreferrer' to='/reports/quantitative-proteomic-and-phosphoproteomic-comparison-RVae00p'>
              scite report
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

const SmartCitationsPanel = () => (
  <div className={styles.container}>
    <div className={styles.citationsSubheader}>
      <Header level={3} className={styles.header}>Read what research articles say about each other</Header>
      <P className={styles.paragraph}> scite is an award-winning platform for discovering and evaluating scientific articles via Smart Citations. Smart Citations allow users to see how a publication has been cited by providing the context of the citation and a classification describing whether it provides supporting or contrasting evidence for the cited claim.</P>
      <div className={styles.citationsIllo}>
        <img alt='smart citations' loading='lazy' className={styles.illoImg} src={`${CONFIG.assetsBaseURL}/images/illustrations/smart-citations-illo-v2.svg?v=1`} />
      </div>
      <div className={styles.btnGroup}>
        <Link to='/search'>
          <Button className={styles.btn} color='blue' bold>
            Start your Search
          </Button>
        </Link>
      </div>
    </div>
    <div className={styles.citationsGrid}>
      <Header level={3} className={styles.extractedHeader}>
        Extracted citations in a report page
      </Header>
      <div className={styles.citation}>
        <SciteClassificationBlock />
        <CitingPaperBlock />
      </div>
      <SmartCitationCarousel />
    </div>
  </div>
)

export default (SmartCitationsPanel)
